import { useQuery } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import React, { useState } from "react";

import * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { fmtDate, int0 } from "../format";
import { batchesKey } from "./useOzonChange";
import type { PlaceEditActions } from "./useOzonDocActions";

/** Что можно сделать с ОДНОЙ строкой товара в режиме изменений: убрать (часть или всю) и
 *  заменить партию.
 *
 *  ⚠️ Обе правки существуют только в режиме: состав после заявки согласован с площадкой, и
 *  уменьшить его молча — это недостача на приёмке. Место с кодом при этом не правится на месте:
 *  бэкенд выносит остаток в НОВОЕ место, а старое оставляет пустым со своим кодом до каскада —
 *  поэтому кнопки доступны и у отправленных мест.
 */
export function ChangeItemActions({
  shipmentId,
  place,
  item,
  busy,
  stamp,
  edits,
}: {
  shipmentId: number;
  place: ozon.SavedPlace;
  item: ozon.SavedItem;
  busy: boolean;
  /** метка последней правки раскладки (`useOzonLayout.stamp`) — ею помечен список партий */
  stamp: number;
  edits: PlaceEditActions;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"menu" | "remove" | "batch">("menu");
  const close = (): void => {
    setOpen(false);
    setMode("menu");
  };
  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setMode("menu");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={busy}
          title="Убрать, уменьшить количество или заменить партию"
          aria-label={`Правки строки ${item.article} в месте №${place.position}`}
          className="rounded px-1 py-0.5 hover:bg-accent disabled:opacity-50"
        >
          <MoreHorizontal className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2 text-xs">
        <div className="mb-1.5 font-medium">
          {item.article} · место №{place.position} · {int0(item.qty)} шт
          {item.batch_no && ` · партия ${item.batch_no}`}
          {item.batch_no && item.channel && ` · ${item.channel}`}
        </div>
        {mode === "menu" && (
          <div className="space-y-1">
            <MenuItem
              label="Убрать / Уменьшить…"
              hint="Сколько штук снять с этой строки. Место с кодом не правится на месте: остаток уедет в новое место, старое останется пустым со своим кодом до применения."
              onClick={() => setMode("remove")}
            />
            <MenuItem
              label="Заменить партию…"
              hint="Партию выбираете вы, портал проверяет свободный остаток и остаточный срок годности. Подбор выбранную партию больше не трогает."
              onClick={() => setMode("batch")}
            />
          </div>
        )}
        {mode === "remove" && (
          <RemoveForm
            item={item}
            busy={busy}
            onRemove={(qty) => {
              edits.remove.mutate({
                position: place.position,
                mp_sku: item.mp_sku,
                qty,
              });
              close();
            }}
            onCancel={() => setMode("menu")}
          />
        )}
        {mode === "batch" && (
          <BatchPicker
            shipmentId={shipmentId}
            place={place}
            item={item}
            busy={busy}
            stamp={stamp}
            onPick={(batch) => {
              edits.rebatch.mutate({
                position: place.position,
                mp_sku: item.mp_sku,
                batch_key: batch.batch_key,
                // пул выбора едет ЦЕЛИКОМ: один ключ партии лежит в остатке под несколькими
                // характеристиками, и по нему одному сервер взял бы первый попавшийся
                char_key: batch.char_key,
              });
              close();
            }}
            onCancel={() => setMode("menu")}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function MenuItem({
  label,
  hint,
  onClick,
}: {
  label: string;
  hint: string;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      className="w-full rounded px-2 py-1.5 text-left hover:bg-accent"
      title={hint}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/** Сколько УБРАТЬ (а не сколько оставить): пусто — строку целиком.
 *
 *  ⚠️ Смысл поля назван прямо на экране. «Количество» рядом с числом читается как «сделать
 *  столько», и ошибка тут стоит недостачи на приёмке или лишнего короба в машине. */
function RemoveForm({
  item,
  busy,
  onRemove,
  onCancel,
}: {
  item: ozon.SavedItem;
  busy: boolean;
  onRemove: (qty: number | null) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [raw, setRaw] = useState("");
  const qty = raw.trim() === "" ? null : Number(raw);
  const bad =
    qty !== null && (!Number.isInteger(qty) || qty <= 0 || qty > item.qty);
  return (
    <div className="space-y-1.5">
      <label className="block">
        Сколько УБРАТЬ, шт (пусто — всю строку, {int0(item.qty)} шт):
        <input
          type="number"
          min={1}
          max={item.qty}
          value={raw}
          autoFocus
          onChange={(e) => setRaw(e.target.value)}
          className="mt-0.5 h-7 w-full rounded border border-input bg-background px-2"
          placeholder={`всё — ${int0(item.qty)}`}
        />
      </label>
      {bad && (
        <div className="text-red-600 dark:text-red-400">
          Введите целое число от 1 до {int0(item.qty)} — это сколько снимаем, а
          не сколько останется.
        </div>
      )}
      <div className="text-muted-foreground">
        Убираем с самой ДАЛЬНЕЙ по сроку партии: ближняя к концу срока обязана
        уехать первой.
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={busy || bad} onClick={() => onRemove(qty)}>
          {qty === null ? "Убрать строку целиком" : `Убрать ${int0(qty)} шт`}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Назад
        </Button>
      </div>
    </div>
  );
}

/** Партии этого товара в порядке FEFO со свободным остатком. Спрашиваются при открытии: остаток
 *  живой, и список, посчитанный при загрузке страницы, к моменту выбора уже неверен. */
function BatchPicker({
  shipmentId,
  place,
  item,
  busy,
  stamp,
  onPick,
  onCancel,
}: {
  shipmentId: number;
  place: ozon.SavedPlace;
  item: ozon.SavedItem;
  busy: boolean;
  stamp: number;
  onPick: (batch: ozon.OzonBatchOption) => void;
  onCancel: () => void;
}): React.ReactElement {
  const q = useQuery({
    // ⚠️ метка правки раскладки — В КЛЮЧЕ: «свободно» считается за вычетом занятого нашей же
    // раскладкой, и после `rebatch` / `remove` / `add` кеш отдал бы числа до правки
    queryKey: batchesKey(shipmentId, place.position, item.mp_sku, stamp),
    queryFn: () =>
      ozon.getOzonChangeBatches(shipmentId, item.mp_sku, place.position),
    staleTime: 30_000,
    retry: false,
  });
  return (
    <div className="space-y-1.5">
      {q.isError && (
        <div className="rounded border border-red-300 bg-red-50 px-2 py-1 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          ⚠ {extractErrorMessage(q.error, "Партии не пришли")}
        </div>
      )}
      {q.isFetching && <div className="text-muted-foreground">Считаем…</div>}
      {q.data?.length === 0 && (
        <div className="text-muted-foreground">
          Годных партий нет: остальные либо без срока, либо не проходят по
          остаточному сроку годности — подбор их всё равно не возьмёт.
        </div>
      )}
      <div className="max-h-56 space-y-0.5 overflow-y-auto">
        {(q.data ?? []).map((b) => (
          <button
            // ключ — ПУЛ целиком: один номер партии приходит дважды, если её остаток лежит под
            // разными характеристиками (каналами), и по одному ключу React считал бы их одним
            key={b.batch_key + b.char_key}
            type="button"
            disabled={busy}
            onClick={() => onPick(b)}
            className={cn(
              "flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-accent disabled:opacity-50",
              // ⚠️ Партия — это ПУЛ (`batch_key` + `char_key`), а не номер: один и тот же номер
              // 1С встречается у разных пулов и даже у разных номенклатур. По одному `batch_no`
              // подсветка «текущей» партии могла отметить чужой пул того же номера.
              b.batch_key === item.batch_key &&
                b.char_key === item.char_key &&
                "bg-accent/60",
            )}
            title={`Свободно ${int0(b.free_qty)} шт с учётом чужих поставок и нашей раскладки`}
          >
            <span className="flex-1">
              {b.batch_no} · до {fmtDate(b.batch_exp)}
              {b.channel && ` · ${b.channel}`}
            </span>
            <span
              className={cn(
                "tabular-nums",
                b.free_qty < item.qty
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-muted-foreground",
              )}
            >
              свободно {int0(b.free_qty)}
            </span>
          </button>
        ))}
      </div>
      <div className="text-muted-foreground">
        Список в порядке FEFO. Не хватит остатка — портал откажет с числами.
      </div>
      <Button size="sm" variant="outline" onClick={onCancel}>
        Назад
      </Button>
    </div>
  );
}
